/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import _ from 'lodash';
import cloneDeep from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import isArray from 'lodash.isarray';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import sortby from 'lodash.sortby';
import uniq from 'lodash.uniq';
import {
  isDesktop
} from '@ion/core/src/capabilities/device';
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage
} from '@ion/core/src/capabilities/storage';
import {findCurrentAccountMatch} from '@ion/core/src/entities/Accounts';
import {isIgnitionOff} from '@ion/core/src/entities/Asset';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {parse} from '@ion/core/src/services/ArcGISUtils';
import {
  currentUser as currentUserApi,
  getAncestorsAccount
} from '@ion/core/src/services/Connect';
import {entityId, resolveHistoryEvents} from '@ion/core/src/services/ConnectUtils';
import Device from '@ion/core/src/services/Device';
import {EventCode} from '@ion/core/src/services/EventCodes';
import {
  AccountLink,
  Asset,
  BaseAsset,
  CameraHistoryEvent,
  CVSAssetType,
  DeviceResultIdentifierType,
  GenericObject,
  HistoryEventByAsset,
  HVSRecord,
  Link,
  User
} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import {
  ScDeviceFeaturesResponse,
  ScResponseType
} from '@ion/core/src/types/services';
import {convertOdometer, getUserUnit} from '@ion/core/src/units/helpers';
import {hasLength} from '@ion/core/src/utils/ArrayUtil';
import {endOfDay, startOfDay} from '@ion/core/src/utils/DateUtil';
import {resolveFilter} from '@ion/dls/src/filters/FilterHelpers';
import {SavedMapViewType, TrackingFilters} from '@ion/dls/src/types/types';
import {getItemValue} from '@/routes/tracking/trackingUtils';
import {TrackingHistoryMapInterface} from '../types/reports';
import {ReportsFilter} from './reducers/reports';
import {fetchDevicesByIdentifiers} from './deviceSearch';
import {getImmediateSubAccountIds, isImmediateSubAccountsOnly} from './subAccounts';

// defines the dashboard preference key
export const DASHBOARD_PREFERENCE = 'iOn-dashboard';

/**
 * Provides a utility for resolving an account id by using the provided id or
 * falling back to the currently persisted account id (user's default account).
 */
export const getAccountId = (getState: Function, accountId?: number): number => {
  return accountId || getState().globalGroup.globalGroupId;
};

/**
 * Provides a utility for resolving an account id by using the provided id or
 * falling back to the currently selected global account id.
 */
export const getGroupId = (getState: Function, accountId?: number): number => {
  return accountId || getState().globalGroup.globalGroupId;
};

/**
 * Provides a utility for getting all subaccount ids for the given account id.
 */
export function getSubAccount(parentAccount, accountIds) {
  parentAccount?.subAccounts?.forEach(sub => {
    const id = entityId(sub?.subaccount?.href || sub?.href);
    accountIds.push(id);
    const subAccounts = sub?.subaccount?.subAccounts || sub?.subAccounts;
    if (subAccounts) {
      getSubAccount(sub?.subaccount || sub, accountIds);
    }
  });
  return accountIds;
};

/**
 * Returns all account ids for the authenticated user account hierarchy.
 */
export const getAccountIds = (getState: Function, accountId?: number): number[] => {
  const {accounts} = getState().initialization;
  const {user}     = getState().login;
  const id         = accountId || entityId(user?.account);
  const accountIds = accounts?.map(({href}) => entityId(href));
  accountIds?.unshift(id);
  return accountIds;
};

/**
 * Returns all account and subaccount ids for the authenticated user account hierarchy.
 */
export const getAllAccountIds = (getState, accountId?: number, includeParent: boolean = true): number[] => {
  const accountIds = [];
  const {globalGroup, globalGroupId, accountHierarchy} = getState().globalGroup;
  const [match] = findCurrentAccountMatch(accountHierarchy, globalGroup);
  if (match?.subAccounts) {
    const subAccounts = getSubAccount(match, []);
    subAccounts && accountIds.push(...subAccounts);
  }
  if (includeParent) {
    const id = accountId || globalGroupId;
    accountIds?.unshift(id);
  }
  return accountIds;
};

/**
 * Returns the account ids used to load the global group's geofences, limited to
 * the immediate sub-accounts for accounts flagged by `isImmediateSubAccountsOnly`,
 * otherwise falling back to the full account hierarchy.
 */
export const getGeofenceAccountIds = (getState: Function): number[] => {
  const {globalGroup, globalGroupId} = getState().globalGroup;
  return isImmediateSubAccountsOnly(globalGroupId)
    ? getImmediateSubAccountIds(globalGroup, globalGroupId)
    : getAllAccountIds(getState);
};

/**
 * Constructs an account hierarchy using the selected group as the top
 * level account
 */
export const getSelectedGroupHierarchy = (getState): AccountLink[] => {
  const {globalGroup, accountHierarchy} = getState().globalGroup;
  const match = findCurrentAccountMatch(accountHierarchy, globalGroup);
  return match;
};

/**
 * Provides an utility for setting the user's default account as parent account and its nested
 * accounts as subAccounts.
 */
export const setUserAccountHierarchy = (getState, accounts) => {
  const userAccount = {...getState().login?.user?.account, subAccounts: []};
  accounts?.forEach(account => {
    const subAccount = {
      subaccount : {...account}
    };
    userAccount.subAccounts.push(subAccount);
  });
  return [userAccount];
};

/**
 * This function allows for retrieving the selected filters value when the filters
 * type is an object, else returns the selected filters.
 */
export const getFilterRequestParams = (selections) => {
  let requestParams = [];
  if (selections[0]?.id) {
    selections.forEach(selected => {
      requestParams.push(resolveFilter(selected)?.value);
    });
  } else {
    requestParams = [...selections];
  }
  return requestParams;
};

/**
 * @function getCheckedAccountIds
 *
 * Provides an utility for retrieving the Checked Account Ids if they exist,
 * otherwise, returns the User's Account Ids Hierarchy
 *
 * import {getCheckedAccountIds} from '../helpers';
 *
 * const checkedGroupIds = getCheckedAccountIds(getState);
 */
export const getCheckedAccountIds = (getState) => {
  const {
    tracking: {
      filters: {
        groups,
        assets: {
          selected: selectedAssets
        },
        devices
      }
    }

  } = getState();
  const {
    selected: selectedDevices
  } = devices || {};
  const includeAssets  = selectedAssets?.length;
  const includeGroups  = groups?.selected?.length;
  const includeDevices = selectedDevices?.length;
  const accountIds     = [...getFilterRequestParams(groups.selected)];
  // to account the case where there are selected assets, but not groups selected.
  if (!includeGroups && (includeAssets || includeDevices)) {
    return accountIds;
  };
  return accountIds?.length > 0 ? accountIds : getAllAccountIds(getState);
};

/**
 * @function getFilteredAssets
 *
 * ```typescript
 * import {getFilteredAssets} from '../helpers';
 *
 * const filteredAssets = getFilteredAssets(assets, accountIds);
 * ...
 *
 *
 * @param assets selected assets in the filter state
 * @param accountIds selected groups ids in the filter state
 *
 */
export const getFilteredAssets = (assets: any[], accountIds: any[]): any[] => {
  return assets?.filter(({accountId}) => !accountIds.find(id => accountId.toString() === id.toString()));
};

/**
 * Given a specific iOn Suite Grid columns path, if the user has chosen to
 * persist their Grid Column selections across sessions and devices, this
 * function will return the user persisted column selections and state for
 * the given grid (if any).
 *
 * ```typescript
 *
 *    import {getGridColumnState, setGridColumnState} from '../helpers';
 *
 *    const PREFERENCE = 'admin-assets';
 *    ...
 *
 *    [INITIALIZE_ADMIN_ASSETS] : (draft) => {
 *      draft.isInitialized = true;
 *      draft.grid.columnState = getGridColumnState(PREFERENCE);
 *    }
 *
 * ```
 *
 * @param   path The user preferences path for the given grid; e.g.
 *          'admin-assets.grid.desktop.columns', etc.
 * @param   forceFetch Allow to fetching the grid setting in user preference forcefully
 * @returns The persisted column state for the given grid.
 */
export const getGridColumnState = (page: string, forceFetch: boolean = false): ColumnState[] | undefined => {
  if (isPreferenceAvailable('persistGridLayouts') || forceFetch) {
    return UserPreferences.get(getPreferencePath(page)) as ColumnState[];
  }
};

/**
 * Given a specific iOn Suite Grid columns path, and the current state of the grid
 * columns for the given path, persists the column state to the path if the user has
 * chosen to persist their Grid Column selections, otherwise returns false.
 *
 * ```typescript
 *
 *    import {getGridColumnState, setGridColumnState} from '../helpers';
 *
 *    const PREFERENCE = 'admin-assets';
 *    ...
 *
 *    [SET_GRID_COLUMNS] : ({grid}: AdminAssetsState, {columnState}) => {
 *      grid.columnState = columnState;
 *      setGridColumnState(PREFERENCE, columnState);
 *    }
 *
 * ```
 *
 * @param   path The user preferences path for the given page; e.g.
 *          'admin-assets', etc.
 * @param   state The current state of the grid columns.
 * @param   autoSaveGrid Explicit check of auto save grid settings in user preference.
 * @returns The persisted column state for the given grid.
 */
export const setGridColumnState = debounce((
  page: string,
  state: ColumnState[],
  autoSaveGrid: boolean = false
): Promise<boolean> => {
  if (autoSaveGrid || isPreferenceAvailable('persistGridLayouts')) {
    return saveGridColumnState(page, state);
  }
  return Promise.resolve(false);
}, 3000);

/**
 * The utility for save grid column state in user preferences based on path.
 *
 *  ```typescript
 *
 *    import {saveGridColumnState} from '../helpers';
 *
 *    const PREFERENCE = 'admin-assets';
 *
 *    saveGridColumnState(PREFERENCE, columnState);
 *
 * ```
 *
 * @param page The user preferences path for the page
 * @param state The current state of the grid columns.
 * @returns The persisted column state for the given grid.
 */
export const saveGridColumnState = (page: string, state: ColumnState[]): Promise<boolean> => {
  return UserPreferences.set(getPreferencePath(page), state, true);
};

/**
 *
 * omitWidthproperty function remove width property in the columnState to eliminate
 * unnecessary update indication on the ag-grid save button.
 *
 * ```typescript
 *
 *  const omitted = omitWidthProperty(state: columnState[]);
 *
 * ```
 *
 */
const omitWidthProperty = (state: ColumnState[]) => {
  if (state) {
    return state.map(elem => ({...elem, width: null}));
  }
};

/**
 * The utility function compares the previous and next state of the Grid column state.
 *
 *  ```typescript
 *
 *    import {hasChangeInColumnState} from '../helpers';
 *
 *    const hasChange = hasChangeInColumnState(preferedColumnState, nextColumnState);
 *
 * ```
 *
 * @param preState The previous grid column state
 * @param nxtState The next grid column state
 * @returns returns true if the state has change, else false
 */
export const hasChangeInColumnState = (preState: ColumnState[], nxtState: ColumnState[]): boolean => {
  return !isEqual(omitWidthProperty(preState), omitWidthProperty(nxtState));
};

/**
 * The utility for set grid column state in redux and user preferences based on path
 * if the user enabled the Automatic Grid saving option.
 *
 *  ```typescript
 *
 *    import {onSetGridColumns} from '../helpers';
 *
 *    const PREFERENCE = 'admin-assets';
 *
 *    onSetGridColumns({
 *      grid,
 *      isLoading  : assets.loading,
 *      preference : PREFERENCE
 *    }, columnState);
 *
 * ```
 *
 * @param grid grid redux state
 * @param isLoading indicates changes while loading
 * @param preference page preference.
 * @param columnState column state which holds the previous and current state
 */
export const onSetGridColumns = ({grid, isLoading, preference}, columnState) => {
  const {
    preferedColumnState,
    nextColumnState,
    autoSaveGrid
  }                = columnState;
  const preCheck   = !isLoading && !autoSaveGrid;
  grid.hasUpdate   = preCheck && hasChangeInColumnState(preferedColumnState, nextColumnState);
  grid.columnState = nextColumnState;
  setGridColumnState(preference, nextColumnState, autoSaveGrid);
  autoSaveGrid && (grid.preferedColumnState = nextColumnState);
};

/**
 * The utility for set grid column state in redux and user preferences based on path.
 *
 *  ```typescript
 *
 *    import {onUpdateGridColumns} from '../helpers';
 *
 *    const PREFERENCE = 'admin-assets';
 *
 *    onUpdateGridColumns({
 *      grid,
 *      preference : PREFERENCE
 *    }, columnState);
 *
 * ```
 *
 * @param grid grid redux state
 * @param preference page preference.
 * @param columnState column state which holds the previous and current state
 */
export const onUpdateGridColumns = ({grid, preference}, columnState) => {
  const {nextColumnState}   = columnState;
  grid.hasUpdate            = false;
  grid.preferedColumnState  = nextColumnState;
  saveGridColumnState(preference, nextColumnState);
};

/**
 * Provides a utility which allows for determining if the UserPreferences API has been
 * initialized and if the given preference is defined.
 */
export const isPreferenceAvailable = (preference?: string) => {
  return UserPreferences.isInitialized() && (preference ? UserPreferences.get(preference) : true);
};

/**
 * Provides a utility which returns current User's notifications displaying prefrences
 */
export const getUserNotificationsPreferences = () => {
  if (UserPreferences.isInitialized()) {
    return UserPreferences.get([
      Preference.showNotifications,
      Preference.showUrgentNotifications,
      Preference.showGeneralNotifications,
      Preference.showTimeRelatedNotifications,
      Preference.showConnectivityNotifications,
      Preference.showScheduledEventsNotifications
    ]);
  }
};

/**
 * @private
 *
 * Provides a convenience function which determines and returns the standardized grid
 * columns preferences path.
 */
const getPreferencePath = (page: string): string => {
  return `${page}:grid.columns.${isDesktop() ? 'desktop' : 'mobile'}`;
};

/**
 * Returns all account and subaccount ids for the authenticated user account hierarchy.
 */
export const getAllAccounts = (getState, accountId?: number, includeParent: boolean = true): Link[] => {
  const accountLinks = [];
  const {accounts} = getState().initialization;
  accounts?.forEach(account => {
    const id = entityId(account?.href);
    accountLinks.push(account);
    if (account?.subAccounts) {
      getSubAccounts(account, accountLinks);
    }
  });
  if (includeParent) {
    const {user}  = getState().login;
    const account = accountId || user?.account;
    accountLinks?.unshift(account);
  }
  return accountLinks;
};

/**
 * Provides a utility for getting all subaccount ids for the given account id.
 */
export function getSubAccounts(parentAccount, accountIds) {
  parentAccount.subAccounts?.forEach(sub => {
    accountIds.push(sub?.subaccount);
    if (sub?.subaccount?.subAccounts) {
      getSubAccounts(sub?.subaccount, accountIds);
    }
  });
  return accountIds;
};

/**
 * The function that will handles the persistence of dashboard saved widget list. The same function will
 * act as both getter and setter function based on the clientToServer flag.
 *
 * ```typescript
 *
 *  import {dashboardPersistence} from './helpers';
 *
 *  dashboardPersistence() // getter function
 *  dashboardPersistence(state, true) // setter function
 *
 * ```
 *
 * @param state dashboard state in redux
 * @param clientToServer indicates the flow of call.
 */
export const dashboardPersistence = (state: GenericObject = {}, clientToServer: boolean = false) => {
  if (clientToServer) {
    return UserPreferences.set(DASHBOARD_PREFERENCE, state, true);
  } else {
    return UserPreferences.get(DASHBOARD_PREFERENCE);
  }
};

export const filterPersistence = (filterID: string, state = {}, clientToServer = false) => {
  if (clientToServer) {
    return UserPreferences.set(filterID, state, true);
  } else {
    return UserPreferences.get(filterID);
  }
};

/**
 * Provides a utility function which resolves the iOn Prime URL.
 */
export const getIONPrimeURL = ({host}, referrer='ionsuite') => {
  let url;
  if (host.indexOf('localhost') <= -1) {
    const pre = host.split('.')[0].replace('https://', '');
    const ext = host.indexOf('co.uk') <= -1 ? 'com' : 'co.uk';
    const env = ['dev', 'qa', 'stg', 'staging'].find(en => pre.indexOf(en) > -1) ? pre : null;
    url = env ? `https://${env}.connect.calamp.${ext}/ion` : `https://connect.calamp.${ext}/ion`;
  } else {
    url = 'http://localhost:3000';
  }
  return `${url}/#/tracking?referrer=${referrer}`;
};

const PREVIOUS_PAGE_STORAGE_KEY = 'previousPage';
const QUERY_PARAMS_STORAGE_KEY = 'queryParams';

/**
 * Provides a utility function to store previous page.
 */
export const storePreviousPage = (page: string, query: string): void => {
  setLocalStorage(PREVIOUS_PAGE_STORAGE_KEY, page);
  setLocalStorage(QUERY_PARAMS_STORAGE_KEY, query);
};

/**
 * Provides a utility function to delete previous page.
 */
export const deletePreviousPage = (): void => {
  deleteLocalStorage(PREVIOUS_PAGE_STORAGE_KEY);
  deleteLocalStorage(QUERY_PARAMS_STORAGE_KEY);
};

/**
 * Defines type for persisted PreviousPage.
 */
export type PreviousPage = {
  route: string,
  query: string
}

/**
 * Provides a utility function to retrieve previous page.
 */
export const getPreviousPage = (): PreviousPage => {
  return {
    route : getLocalStorage(PREVIOUS_PAGE_STORAGE_KEY),
    query : getLocalStorage(QUERY_PARAMS_STORAGE_KEY)
  };
};

const TRACKING_EXTENT = 'trackingExtent';

/**
 * Provides a utility function to store the Extent of Tracking Page
 * into Local Storage.
 */
export const storeTrackingExtent = (trackingExtent: string): void => {
  setLocalStorage(TRACKING_EXTENT, trackingExtent);
};

/**
 * Provides a utility function to return the Extent of Tracking Page
 * from Local Storage.
 */
export const getTrackingExtent = (): string => {
  return getLocalStorage(TRACKING_EXTENT);
};

/**
 * Provides a utility function to delete the Extent of Tracking Page
 *  from Local Storage.
 */
export const deleteTrackingExtent = (): void => {
  deleteLocalStorage(TRACKING_EXTENT);
};

/**
 * Parse utility for ScDevice JSON response. This will transform data as required by sidesheet components.
 * ```typescript
 *   import {parseSciCollection} from '@ion/ion-suite/src/store/helpers';
 *
 *   consr response = await axios({...});
 *   const data = parseSciCollection(response);
 * ```
 */
export const parseSciCollection = (response: ScResponseType) => {
  const collections: ScDeviceFeaturesResponse[] = parse(response) ?? [];
  const devices = Device.getDevicesFromCollection(collections);
  return devices;
};

/**
 *
 * updateTripVisible update columnState in redux store to be matching
 * with updated showTripVisible changes
 *
 * ```typescript
 *
 * import { updateTripVisible };
 *
 * grid.columnState = updateTrivisible(grid.columnState, showTripVisible);
 *
 * ```
 */
export const updateTripVisible = (
  columnState: ColumnState[],
  showTripVisible: boolean): ColumnState[] => {
  const COLUMN_ID = 'tripGroup';
  const target: ColumnState = columnState.find(({colId}) => colId === COLUMN_ID);
  if (target) {
    target.rowGroup = showTripVisible;
    return columnState.map(state => state.colId === COLUMN_ID ? target : state);
  }
  return [...columnState];
};

/**
 *
 * createRequestAssets creates list of uniq assets returned from the HVS request
 * @param history: HVSRecord[]
 * @returns CVSAssetType[]
 *
 * ```typescript
 *
 * import { createRequestAssets };
 * ...
 *
 * const requestAssets = createRequestAssets(history);
 *
 * ```
 */
export const createRequestAssets = (history: HVSRecord[]): CVSAssetType[] => {
  const assetListMap = {};
  // create a Set of asset list hash map from the HVS response
  // uniq to each asset by assetId
  history.forEach((event) => {
    if (!assetListMap[event?.assetId]) {
      assetListMap[event?.assetId] = event;
    }
  });
  return Object.values(assetListMap);
};

/**
 * Utility function to get asset name for sorting
 */
const getName = (item: CVSAssetType | Device) => {
  return item?.assetName?.trim() || item?.deviceName?.trim();
};

/**
 * Utility function to sort the assets list based on sort order
 */
export const sort = (assets, asc: boolean) => {
  if (assets?.length) {
    const sortedList = assets?.slice(0).sort((a, b) => {
      const nameA = getName(getItemValue(a))?.toLowerCase();
      const nameB = getName(getItemValue(b))?.toLowerCase();
      return ascendingOrderValidator(nameA, nameB);
    });
    return !asc ? sortedList.reverse() : sortedList;
  }
  return [];
};

// Creating a collator object
const collator = new Intl.Collator(undefined, {
  ignorePunctuation : true
});

/**
 *
 * Utility to validate ascending order
 *
 * ```typescript
 *
 * import {ascendingOrderValidator} from '../helpers';
 *
 * return ascendingOrderValidator(a, b);
 *
 * ```
 */
export const ascendingOrderValidator = (valueA: string, valueB: string) => collator.compare(valueA, valueB);

/**
 *
 * getDevicesByESN returns a list of serial numbers from a device's tag report.
 *
 * ```typescript
 *
 * import {getDevicesByEsn} from '../helpers';
 *
 * const ESNs = getDevicesByESN(device);
 *
 * ```
 *
 */
/**
 * Resolves a device's id from its link, or its own `id`/`deviceId` - also
 * when given as a string, which `entityId` alone reads as `undefined` (and a
 * request went out for `/devices/undefined`, e.g. for an SC device). Only a
 * valid id comes back.
 *
 * ```typescript
 * import {getDeviceEntityId} from '@/store/helpers';
 *
 * ...
 *
 * const deviceId = getDeviceEntityId(device);
 * ```
 *
 * @param device The device, or its link.
 * @returns      Its id, or `undefined` without a valid one.
 */
export const getDeviceEntityId = (device: any): number | undefined => {
  const parsed = Number(entityId(device) ?? device?.id ?? device?.deviceId);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

export const getDevicesByESN = (device: BaseAsset | Asset) => {
  const allTagReports = device?.tagReports?.map(tagReport => tagReport?.serialNumber);
  const serialNumbers = uniq(allTagReports).join(' ');
  return [serialNumbers];
};

const getFilteredEventHistory = (history: HVSRecord[], selectedEvents: EventCode[]) => {
  let filteredHistory: HVSRecord[];
  if (history?.length) {
    const historyCopy: HVSRecord[] = cloneDeep(history);
    filteredHistory = historyCopy.reduce((acc, item) => {
      const {eventCode, geofenceType} = item;
      const matchedEvent = !!(selectedEvents?.find(event => (
        event.eventCode === eventCode || event.eventType === geofenceType
      )));
      item.gridVisibility = matchedEvent;
      item.mapVisibility  = matchedEvent || isIgnitionOff(item);
      return [...acc, item];
    }, []);
  }
  return filteredHistory;
};

const onGroupHistory = (assetHistoryHash) => {
  let group = 0;
  let sortedHistory;
  Object.values(assetHistoryHash)?.forEach((events: HVSRecord[]) => {
    group++;
    // if first event from the HVS response has ignition === true,
    // include them in the first tripGroup.
    let inTrip = events[0]?.ignition === 'true';
    events?.forEach((item) => {
      // when igOn, start new group
      if (item.ignition === 'true') {
        inTrip = true;
      };
      if (inTrip) {
        // only assign tripGroup to events between igOn to igOff
        item.tripGroup = group;
      };
      // when igOff end the group
      if (item.ignition === 'false') {
        inTrip = false;
        // update the current tripGroup to next group
        group++;
      };
      item.tripGroup = item.tripGroup ?? null;
    });
    // If Boundary filter is active on the map then the history data
    // should be sorted by eventTime and deviceMessageSequenceNumber
    sortedHistory = [...(sortedHistory || []), ...(sortby(events, ['eventTime', 'deviceMessageSequenceNumber']) || [])];
  });
  return sortedHistory || [];
};

/**
 *
 * findIgnitionData returns a boolean whether event data contains ignition data.
 * This utility function is used to disable showTripVisible for assets
 * don't have engines.
 *
 * ```typescript
 *
 * import { findIgnitionData } from '../helpers';
 *
 * const isTripGroupCreated = findIgnitionData(event);
 *
 * ```
 *
 */
export const findIgnitionData = ({ignition, eventType}: HVSRecord): boolean => (
  ignition === 'true' ||
  eventType === HistoryEventByAsset.IGON ||
  eventType === HistoryEventByAsset.IGOFF
);

interface AugmentedHistoryDataType {
  history: HVSRecord[]
  filters: ReportsFilter
  user: User
  showEventsBetweenTrips: boolean
  onToggleShowTripVisible: Function
  onToggleShowEventsBetweenTrips: Function
  retrieveDistanceTraveled: Function;
  retrieveDrivingTime: Function;
  retrieveIdlingTime: Function;
  geofenceEvents: any[];
  crashboxxEvents: any[];
  severeEventsList: number[];
}

export const augmentHistoryData = ({
  history,
  retrieveDistanceTraveled,
  retrieveDrivingTime,
  retrieveIdlingTime,
  geofenceEvents,
  crashboxxEvents,
  severeEventsList
}: AugmentedHistoryDataType) => {
  if (history?.length) {
    const assetHistoryHash: TrackingHistoryMapInterface = {};
    resolveHistoryEvents({
      history,
      crashboxxEvents,
      geofenceEvents
    }).sort((a, b) => a?.eventTime - b?.eventTime)
      .forEach((item) => {
        const {
          odometer,
          gpsOdometer,
          vbOdometer,
          systemOfUnits
        } =
        item ?? {};

        item = {
          ...item,
          odometer       : convertOdometer(odometer, systemOfUnits, getUserUnit()),
          gpsOdometer    : convertOdometer(gpsOdometer, systemOfUnits, getUserUnit()),
          vbOdometer     : convertOdometer(vbOdometer, systemOfUnits, getUserUnit()),
          gridVisibility : true,
          mapVisibility  : true,
          isSevereEvent  : severeEventsList.includes(item.eventCode)
        };

        const asset = assetHistoryHash[item?.assetId];
        assetHistoryHash[item?.assetId] = asset ? [...asset, item] : [item];
      });

    /**
     * Adds isCameraOnly property to the event objects to identify Assets that are
     * exclusively equipped with camera devices so that we can complete calculations
     * specific to camera-only assets for our Summary Cards.
     */
    updateCameraOnlyProperty(assetHistoryHash);

    /**
       * Sort the events so the crash events get inserted at the right moment
       * Builds out an asset hash map for setting history events to the correct trip group
       */
    const sortedHistory = onGroupHistory(assetHistoryHash);
    retrieveDistanceTraveled(assetHistoryHash);
    retrieveDrivingTime(assetHistoryHash);
    retrieveIdlingTime(assetHistoryHash);

    return sortedHistory;
  } else {
    retrieveDistanceTraveled(0);
    retrieveDrivingTime(0);
    retrieveIdlingTime(0);
  }
  return [];
};

/**
* Defines a utility to return array of ids from Ancestor's accounts
*
* ```typescript
*
* import {createAncestorList} from '../helpers';
*
* const ancestorIds = createAncestorList(accountId);
*
* ```
*/
export const createAncestorList = (currentGroupId: number): Promise<GenericObject> => {
  return getAncestorsAccount(currentGroupId).then(result => {
    const ancestorIds = {};
    Object.values(result).map(({href}) => {
      const value = entityId(href);
      ancestorIds[value] = value;
    });
    return ancestorIds;
  });
};

/**
 * Given a group heirarchy key identifier, parses the parent key
 * from the given key and returns the result.
 *
 * @public
 * @method  getParentKey
 * @param   {string} key the group heirarchy key to be parsed
 * @returns {String} the parsed parent key, if available.
 */
const getParentKey = (key) => {
  const lastDashIndex = key.lastIndexOf('-');
  if (lastDashIndex > 0) {
    return key.substr(0, lastDashIndex);
  }
  return '';
};

/**
 * removeParentKey is a utility function that remove all
 * parent key from the checkedKeys.
 *
 * ```typescript
 * import {removeParentKey} from './helpers';
 *
 * const updatedKey = removeParentKey(key);
 *
 * ```
 *
 * @checkedKeys string[] an array of keys
 * @return string[] an array of filtered keys
 */
export const removeParentKey = (checkedKeys: string[] = []): string[] => {
  let validKeys = checkedKeys.filter(Boolean);
  if (validKeys.length) {
    validKeys.map(key => {
      const isPhantom = key[0] === '-';
      const parentKey = getParentKey(key);
      if (!isPhantom) {
        validKeys = removeParent(parentKey, validKeys);
      }
    });
  }
  return validKeys;
};

/**
 * removeParent filters the parent key from the checkedKeys
 * It runs recursively until it doesn't find the parent key anymore.
 *
 */
const removeParent = (parentKey, checkedKeys) => {
  if (parentKey?.length) {
    const isParentFound = checkedKeys.indexOf(parentKey) > -1;
    if (isParentFound) {
      const updatedKeys = checkedKeys.filter(key => key !== parentKey);
      const newParentKey = getParentKey(parentKey);
      return removeParent(newParentKey, updatedKeys);
    }
  }
  return checkedKeys;
};

export const filterSavedMapViews = (id: string, mapViews: SavedMapViewType[]) => {
  const filteredMapViews = mapViews.filter((savedMapView) => savedMapView.id !== id) || [];
  UserPreferences.set(Preference.savedMapViews, filteredMapViews, true);
  return filteredMapViews;
};

type ReportsParamsType = {
  filters: ReportsFilter | TrackingFilters;
  boundary?: number[][];
}

export const getReportParams = ({
  filters,
  boundary
}: ReportsParamsType) => {
  if (!isEmpty(filters)) {
    let fetchedGroups: number[] = [];
    const {
      groups : {
        selected: selectedGroups = []
      } = {},
      assetTypes: {
        selected: selectedTypes = []
      } = {},
      assetCategories: {
        selected: selectedCategories = []
      } = {},
      assets : {
        selected: selectedAssets = []
      } = {},
      devices : {
        selected: selectedDevices = []
      } = {},
      date: {
        startDate,
        endDate
      } = {},
      events: {
        selected: selectedEvents = []
      } = {}
    } = filters;

    selectedGroups?.forEach((group) => {
      fetchedGroups.push(group.id);
    });
    // this will remove all redundants
    fetchedGroups = Array.from(new Set(fetchedGroups));
    const filteredAssets = getFilteredAssets(selectedAssets, fetchedGroups);
    const filteredDevices = getFilteredAssets(selectedDevices, fetchedGroups);

    // convert assets / devices into array of its ids and pass them in params below.

    const params = {
      groups     : fetchedGroups,
      geometry   : boundary?.length ? boundary : null,
      categories : selectedCategories,
      assets     : filteredAssets,
      devices    : filteredDevices,
      types      : selectedTypes,
      start      : startDate || startOfDay(),
      end        : endDate || endOfDay(),
      events     : selectedEvents
    };

    const filtersApplied = hasLength(
      selectedTypes,
      selectedCategories,
      filteredAssets,
      fetchedGroups,
      boundary,
      selectedDevices
    ) || Boolean((startDate || endDate));

    return {params, filtersApplied};
  }
};

/**
 * getRootNode returns nodes in array format
 *
 * ```typescript
 * import {getRootNode} from './helpers';
 *
 * const rootNode = getRootNode(nodes);
 * ...
 *
 * ```
 *
 */
export const getRootNode = nodes => {
  return isArray(nodes) ? nodes : [nodes];
};

/**
 * Utility function to combine existing list of Device instances with existing Device instance.
 * This will ensure existing list is maintained with latest updates merged.
 *  ```typescript
 * import {mergeLatestUpdates} from './helpers;
 * const newData = mergeLatestUpdates(existingAssets, updates);
 *  ```
 */
export const mergeLatestUpdates = (currentUpdates: Device[], latestUpdates: Device[]): Device[] => {
  const tempCurrentUpdates = [...(currentUpdates ?? [])];
  latestUpdates?.forEach((update: Device) => {
    const index = tempCurrentUpdates.findIndex((currentUpdate: Device) => currentUpdate.id() === update.id());
    if (index !== -1) {
      tempCurrentUpdates.splice(index, 1, update);
    } else {
      tempCurrentUpdates.push(update);
    }
  });
  return tempCurrentUpdates;
};

/**
 * Utility function to check if the accountid is present in the current global group account hirarchy.
 *  ```typescript
 *
 *   import {containsInGroupHierarchy} from './helpers;
 *
 *   const isAccountIdfound = containsInGroupHierarchy(1234, globalstate); // true or false
 *
 *  ```
 */
export const containsInGroupHierarchy = (accountId: number, getState: Function) => (
  getAllAccountIds(getState, null, true)?.includes(accountId)
);

/**
 * @private
 * Checks if all events for an Asset have deviceCategory of 'CAMERA'.
 *
 * @param {Array} items - The array of events
 * @returns {boolean} - Returns true if every item in the array has deviceCategory as 'CAMERA', false otherwise.
 */
const areAllDevicesCameras = (events: CameraHistoryEvent[]): boolean => {
  return events.every(event => event?.deviceCategory === 'CAMERA');
};

/**
 * @private
 * Updates the augmented event with a new 'isCameraOnly' property within the provided asset history hash.
 * This property is set based on whether all devices in the group are cameras.
 *
 * @param {Object} assetHistory - The asset history hash object where each key is an assetId and the value is
 * an array events.
 */
const updateCameraOnlyProperty = (assetHistory: HVSRecord) => {
  Object.keys(assetHistory).forEach(assetId => {
    const allCamera = areAllDevicesCameras(assetHistory[assetId]);
    assetHistory[assetId].forEach(item => {
      item.isCameraOnly = allCamera;
    });
  });
};

/**
 * Utility function to check current page based on params.
 *  ```typescript
 *
 *   import {isCurrentPage} from './helpers;
 *
 *   isCurrentPage(current, route.TRACKING); // true or false
 * 
 *  ``` 
 */
export const isCurrentPage = (currentPage: string, page: string) => {
  return currentPage?.slice(1, currentPage?.length) === page;
};

/**
 * Ensures that the given user object has the `systemOfUnits` property set.
 * If it's missing, it fetches the current user from the API and assigns the systemOfUnits from there.
 *
 * ```typescript
 *
 *   import {ensureUserSystemOfUnits} from './helpers;
 *
 *   const currentUser = ensureUserSystemOfUnits(user);
 * 
 *  ``` 
 * 
 * @param user - The user object to check and possibly update.
 * @returns The user object with a guaranteed `systemOfUnits` property.
 */
export const ensureUserSystemOfUnits = async(user: User) => {
  if (!user.systemOfUnits) {
    const currentUser  = await currentUserApi();
    user.systemOfUnits = currentUser.systemOfUnits;
  }
  return user;
};

/**
 * The device links carried by an Asset expose only the device's href, title
 * and esn; neither the Serial Number nor the IMEI is included by the Assets
 * search. This utility resolves the full Device entity for every device paired
 * to the given Assets and merges the absent identifiers onto each link, which
 * allows the Asset import template to populate a distinct column per
 * identifier for devices which are not identified by an esn, such as Cameras.
 *
 * The Devices are retrieved by their own ids through the Device Smart Search
 * API. Only the Devices actually paired to the exported Assets are requested,
 * in place of every Device held across the account hierarchy ...
 *
 * ```typescript
 *
 *  import {resolveAssetDeviceIdentifiers} from './helpers';
 *
 *  const enriched = await resolveAssetDeviceIdentifiers(assets, accountId, true);
 *
 * ```
 *
 * @param assets The Assets whose paired device links are to be resolved.
 * @param accountId The account from which the Devices are to be retrieved.
 * @param includeSubGroups When `true`, Devices across the account hierarchy
 *        are retrieved, matching the scope of the exported Assets.
 * @returns The given Assets, whose device links carry every resolved
 *          identifier.
 */
export const resolveAssetDeviceIdentifiers = async(
  assets: Asset[],
  accountId: number,
  includeSubGroups = false
): Promise<Asset[]> => {
  const deviceIds = uniq(
    assets?.flatMap(({devices}) => (devices || []).map(link => entityId(link as Link))) || []
  ).filter(Boolean);
  if (deviceIds.length) {
    const devices  = await fetchDevicesByIdentifiers({
      accountId,
      includeSubGroups,
      identifiers    : deviceIds,
      identifierType : DeviceResultIdentifierType.ID
    });
    const resolved = new Map(devices?.map(device => [device?.id, device]));
    assets.forEach(asset => {
      asset.devices = asset.devices?.map((link: GenericObject) => {
        const device = resolved.get(entityId(link));
        return device ? {
          ...link,
          esn          : link?.esn || device?.esn,
          serialNumber : device?.serialNumber,
          imei         : device?.imei
        } : link;
      });
    });
  }
  return assets;
};
